import React from 'react';
import { PaperclipIcon, DownloadIcon, GridIcon, FolderIcon, CheckCircleIcon, XCircleIcon } from './Icons';

const AttachmentsModePanel = ({
  attachmentSource, setAttachmentSource,
  attachmentIds, setAttachmentIds,
  attachmentExcelRef,
  onLinkExcelRange,
  savePath, setSavePath,
  onBrowseFolder,
  loading,
  onDownload,
  attachmentResults
}) => {
  const canDownload = attachmentSource === 'manual' 
    ? attachmentIds.trim().length > 0 
    : attachmentExcelRef.length > 0;
  
  return (
    <div className="bg-gray-800 p-4 rounded shadow-sm border border-purple-500/30 mb-6">
      <div className="flex items-center gap-2 mb-4 text-purple-400 bg-purple-500/10 p-2 rounded border border-purple-500/20">
        <PaperclipIcon />
        <span className="text-xs font-bold uppercase">Download Attachments</span>
      </div>
      
      <div className="mb-4">
        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Attachment IDs Source</label>
        <div className="flex gap-2 mb-3">
          <button 
            onClick={() => setAttachmentSource('manual')} 
            className={`flex-1 py-2 px-3 rounded text-xs font-medium border ${attachmentSource === 'manual' ? 'bg-purple-500/20 border-purple-500/50 text-purple-400' : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'}`}
          >
            Enter Manually
          </button>
          <button 
            onClick={() => setAttachmentSource('excel')} 
            className={`flex-1 py-2 px-3 rounded text-xs font-medium border ${attachmentSource === 'excel' ? 'bg-purple-500/20 border-purple-500/50 text-purple-400' : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600'}`}
          >
            From Excel Column
          </button>
        </div>
        
        {attachmentSource === 'manual' ? (
          <div>
            <textarea 
              className="w-full p-2 border border-gray-600 rounded text-sm font-mono bg-gray-700 text-gray-200" 
              rows={4} 
              placeholder="Enter attachment IDs..." 
              value={attachmentIds} 
              onChange={(e) => setAttachmentIds(e.target.value)} 
            />
            <p className="text-[10px] text-gray-500 mt-1">
              {attachmentIds.split(/[,\n]/).filter(id => id.trim()).length} ID(s) entered
            </p>
          </div>
        ) : (
          <div>
            <div className="flex gap-2">
              <input 
                type="text" 
                readOnly 
                className="flex-1 p-2 border border-gray-600 rounded text-sm bg-gray-700 font-mono text-gray-300" 
                placeholder="Select a range in Excel..." 
                value={attachmentExcelRef} 
              />
              <button 
                onClick={onLinkExcelRange} 
                className="px-4 py-2 bg-purple-500 text-gray-900 rounded text-sm font-medium hover:bg-purple-400 flex items-center gap-1"
              >
                <GridIcon /> Link
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">
              Select a column containing attachment IDs, then click Link
            </p>
          </div>
        )}
      </div>
      
      <div className="mb-4">
        <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Save Location</label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input 
              type="text" 
              className="w-full p-2 pl-8 border border-gray-600 rounded text-sm bg-gray-700 text-gray-200" 
              placeholder="C:\Users\You\Downloads" 
              value={savePath} 
              onChange={(e) => setSavePath(e.target.value)} 
            />
            <div className="absolute left-2 top-2.5 text-gray-500"><FolderIcon /></div>
          </div>
          <button 
            onClick={onBrowseFolder}
            className="px-3 py-2 bg-gray-600 border border-gray-500 rounded text-sm font-medium text-gray-300 hover:bg-gray-500"
          >
            Browse
          </button>
        </div>
        <p className="text-[10px] text-gray-500 mt-1">Files will be saved to this folder on your computer</p>
      </div>
      
      <button 
        onClick={onDownload} 
        disabled={loading || !canDownload} 
        className="w-full py-3 px-4 bg-purple-500 hover:bg-purple-400 disabled:bg-gray-600 text-gray-900 rounded-md shadow-sm text-sm font-bold flex items-center justify-center gap-2"
      >
        <DownloadIcon /> {loading ? 'Downloading...' : '⚡ Download Attachments'}
      </button>
      
      {attachmentResults.length > 0 && (
        <div className="mt-4 border border-gray-700 rounded overflow-hidden">
          <div className="bg-gray-700 px-3 py-2 border-b border-gray-600">
            <span className="text-xs font-bold text-gray-300">Download Results</span>
          </div>
          <div className="max-h-48 overflow-y-auto bg-gray-800">
            {attachmentResults.map((result, idx) => (
              <div 
                key={idx} 
                className={`px-3 py-2 text-xs border-b border-gray-700 last:border-b-0 flex items-center gap-2 ${result.status === 'success' ? 'bg-green-500/10' : 'bg-red-500/10'}`}
              >
                {result.status === 'success' 
                  ? <span className="text-green-400"><CheckCircleIcon /></span> 
                  : <span className="text-red-400"><XCircleIcon /></span>
                }
                <span className="font-mono text-gray-300">{result.attachmentId}</span>
                {result.status === 'success' && result.files && (
                  <span className="text-gray-500 ml-auto">{result.files.length} file(s)</span>
                )}
                {result.status === 'error' && (
                  <span className="text-red-400 ml-auto truncate max-w-[200px]" title={result.message}>
                    {result.message}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttachmentsModePanel;